import React, { useState, useEffect } from 'react';
import { registry } from '../../core/Registry.js';
import { BaseWidget } from '../BaseWidget.jsx';

export function PostGridRender({ node, isLiveMode }) {
  const content = node.content || {};
  
  const postType = content.postType || 'pages'; // pages, tariffs, hardware (später)
  const limit = parseInt(content.limit, 10) || 6;
  const columns = parseInt(content.columns, 10) || 3;
  const showExcerpt = content.showExcerpt !== 'false';
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Im echten Backend laden wir die Seiten über die API
    const fetchPosts = async () => {
      try {
        setLoading(true);
        // Da die Datenbank oft auf :5051 läuft
        const res = await fetch(`http://localhost:5051/api/admin/designer/pages?limit=${limit}`);
        if (!res.ok) throw new Error('API Fehler');
        const data = await res.json();
        // Fallback falls db leer, oder kein Array kommt
        if (Array.isArray(data)) {
           setPosts(data.slice(0, limit));
        } else {
           setPosts([]);
        }
      } catch (err) {
        console.error('PostGrid Error:', err);
        setError('Konnte Posts nicht laden.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [limit, postType]);

  // Für den Designer-Modus ohne DB-Verbindung (oder bei Errors) generieren wir Placeholder
  const displayPosts = (posts.length > 0 && !error) ? posts : Array.from({ length: 3 }).map((_, i) => ({
    id: `placeholder-${i}`,
    title: `Beispiel Artikel ${i+1}`,
    slug: `beispiel-${i+1}`,
    status: 'draft',
    meta: {
      coverImage: `https://images.unsplash.com/photo-${1500000000000 + i}?q=80&w=400&auto=format&fit=crop`,
      excerpt: 'Dies ist ein automatisch generierter Vorschautext für das Post Grid Layout Modul.'
    }
  }));

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {isLiveMode && loading && (
         <div style={{ color: '#00A9E0', textAlign: 'center', padding: '20px' }}>Lade Einträge...</div>
      )}
      
      {!isLiveMode && (
         <div style={{ 
           marginBottom: '10px', fontSize: '12px', color: '#94a3b8', 
           padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px' 
         }}>
           Post Grid Widget (Datenquelle: {postType}, max. {limit} Einträge)
         </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: '24px'
      }}>
        {displayPosts.map(post => {
           const cover = (post.meta && post.meta.coverImage) ? post.meta.coverImage : 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=400&fit=crop';
           const excerpt = (post.meta && post.meta.excerpt) ? post.meta.excerpt : 'Keine Beschreibung verfügbar.';

           return (
             <a 
               key={post.id} 
               href={isLiveMode ? `/${post.slug}` : '#'}
               style={{ 
                 display: 'flex', flexDirection: 'column',
                 textDecoration: 'none', color: 'inherit',
                 background: 'rgba(255,255,255,0.02)',
                 border: '1px solid rgba(255,255,255,0.05)',
                 borderRadius: '12px', overflow: 'hidden',
                 transition: 'transform 0.2s, box-shadow 0.2s'
               }}
               onMouseEnter={(e) => {
                 e.currentTarget.style.transform = 'translateY(-4px)';
                 e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.5)';
               }}
               onMouseLeave={(e) => {
                 e.currentTarget.style.transform = 'translateY(0)';
                 e.currentTarget.style.boxShadow = 'none';
               }}
             >
                {/* Bild */}
                <div style={{ width: '100%', height: '160px', overflow: 'hidden' }}>
                  <img src={cover} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                
                {/* Text */}
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--tef-primary, #00A9E0)', fontWeight: 'bold', marginBottom: '8px' }}>
                    {post.status.toUpperCase()}
                  </div>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', color: '#f8fafc', lineHeight: '1.4' }}>
                    {post.title}
                  </h3>
                  {showExcerpt && (
                    <p style={{ margin: 0, fontSize: '14px', color: '#cbd5e1', lineHeight: '1.6', flexGrow: 1 }}>
                      {excerpt}
                    </p>
                  )}
                  <div style={{ marginTop: '20px', color: 'var(--tef-primary, #00A9E0)', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Weiterlesen <span>→</span>
                  </div>
                </div>
             </a>
           )
        })}
      </div>
    </div>
  );
}

registry.register('PostGrid', {
  label: 'Seiten / Post Grid (Loop)',
  icon: '🗂️', 
  category: 'advanced', // oder cms
  component: PostGridRender,
  
  defaultProps: {
    content: {
      postType: 'pages',
      limit: '6',
      columns: '3',
      showExcerpt: 'true'
    },
    style: {
      desktop: {
        marginBottom: '40px'
      }
    }
  },

  inspectorControls: [
    {
      tab: 'content',
      groups: [
        {
          title: 'Abfrage (Query)',
          fields: [
            { name: 'content.postType', label: 'Eintrags-Typ', type: 'select', options: ['pages', 'news', 'tariffs'] },
            { name: 'content.limit', label: 'Anzahl (Max)', type: 'select', options: ['3', '6', '9', '12'] }
          ]
        },
        {
          title: 'Layout & Anzeige',
          fields: [
            { name: 'content.columns', label: 'Spalten (Desktop)', type: 'select', options: ['1', '2', '3', '4'] },
            { name: 'content.showExcerpt', label: 'Textauszug (Excerpt) zeigen?', type: 'select', options: ['true', 'false'] }
          ]
        }
      ]
    }
  ]
});
